import { getMemberInfo, getMemberInfoForOwner, updateMemberInfo } from '../../api/memberInfoApi';
import { useMemberInfoStore } from '../../store/memberInfoStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { MemberInfoData } from '../../dto/EntityRepository';
import { MemberInfoResponse } from '../../dto/ResDtoRepository';
import { UpdateMemberInfoReq } from '../../dto/ReqDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';


interface UseMemberInfoStateResult {
  memberInfo: MemberInfoData | null;
  memberInfoOwnerAui: string | null;
  memberInfoScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseMemberInfoActionsResult {
  getMemberInfo: () => Promise<void>;
  getMemberInfoOwner: () => Promise<void>;
  updateMemberInfo: (
    data: UpdateMemberInfoReq,
    options?: { notify?: boolean },
  ) => Promise<boolean>;
  updateEngVisible: (data: UpdateMemberInfoReq) => Promise<void>;
}

export const useMemberInfoState = (): UseMemberInfoStateResult => {
  const memberInfo = useMemberInfoStore((state) => state.memberInfo);
  const memberInfoOwnerAui = useMemberInfoStore((state) => state.memberInfoOwnerAui);
  const memberInfoScope = useMemberInfoStore((state) => state.memberInfoScope);
  const status = useMemberInfoStore((state) => state.status);

  return { memberInfo, memberInfoOwnerAui, memberInfoScope, status };
};

export const useMemberInfoActions = (): UseMemberInfoActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const beginMemberInfoFetch = useMemberInfoStore((state) => state.beginMemberInfoFetch);
  const setMemberInfo = useMemberInfoStore((state) => state.setMemberInfo);
  const setMemberInfoError = useMemberInfoStore((state) => state.setMemberInfoError);
  const setMemberInfoEngVisibleAfterUpdate = useMemberInfoStore(
    (state) => state.setMemberInfoEngVisibleAfterUpdate,
  );
  const { setUpdatedTempAlert } = useTempAlertStore();
  const withApiHandler = useApiWrapper();

  const handleMemberInfoSuccess = (
    response: MemberInfoResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setMemberInfo(response.data, ownerAui, scope);
  };

  const handleUpdateMemberInfoSuccess = (
    response: MemberInfoResponse,
    ownerAui: string,
    notify: boolean,
  ): true => {
    setMemberInfo(response.data, ownerAui, 'owner');
    if (notify) setUpdatedTempAlert();
    return true;
  };

  const handleUpdateEngVisibleSuccess = (response: MemberInfoResponse) => {
    setMemberInfoEngVisibleAfterUpdate(response.data);
    setUpdatedTempAlert();
  };

  const fetchMemberInfo = async (aui: string, scope: ResourceScope) => {
    beginMemberInfoFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getMemberInfoForOwner(aui) : getMemberInfo(aui)),
        (response) => handleMemberInfoSuccess(response, aui, scope),
      );
    } catch (error) {
      setMemberInfoError();
      throw error;
    }
  };

  const handleMemberInfoRequest = async (
    action: 'get' | 'get_for_owner' | 'updateEngVisible',
    data?: UpdateMemberInfoReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'updateEngVisible':
          return runCurrentArchiveRequest(
            aui,
            () => updateMemberInfo(aui, data as UpdateMemberInfoReq),
            handleUpdateEngVisibleSuccess,
          );
        case 'get_for_owner':
          return fetchMemberInfo(aui, 'owner');
        case 'get':
        default:
          return fetchMemberInfo(aui, 'public');
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const handleUpdateMemberInfoRequest = async (
    data: UpdateMemberInfoReq,
    options: { notify?: boolean } = {},
  ): Promise<boolean> => {
    if (!pageOwnerAui) return false;
    const aui = pageOwnerAui;
    const { notify = true } = options;
    const result = await withApiHandler(
      () => runCurrentArchiveRequest(
        aui,
        () => updateMemberInfo(aui, data),
        (response) => handleUpdateMemberInfoSuccess(response, aui, notify),
      ),
      [aui, 'update', data],
    );

    return result === true;
  };

  return {
    getMemberInfo: () => handleMemberInfoRequest('get'),
    getMemberInfoOwner: () => handleMemberInfoRequest('get_for_owner'),
    updateMemberInfo: handleUpdateMemberInfoRequest,
    updateEngVisible: (data: UpdateMemberInfoReq) => handleMemberInfoRequest('updateEngVisible', data),
  };
};

/** 기존 호출부 호환: state + actions */
export const useMemberInfo = (): UseMemberInfoStateResult & UseMemberInfoActionsResult => ({
  ...useMemberInfoState(),
  ...useMemberInfoActions(),
});
